import React, { useRef, useState } from "react";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import { ReactMediaRecorder } from "react-media-recorder";
import LoaderIcon from "./LoaderIcon";
import { CiMicrophoneOff, CiMicrophoneOn } from "react-icons/ci";
import AudioPlayer from "./AudioPlayer";

const VoiceRecorder = ({ recordings = [], onChange, onUpload }) => {
  const [showRecorder, setShowRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  //  Start Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      setIsRecording(true);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.start();
    } catch (error) {
      console.error("Mic access denied:", error);
    }
  };

  // 🛑 Stop & Save
  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) return;

    recorder.stop();
    setIsRecording(false);

    recorder.onstop = () => {
      // Raw blob
      const rawBlob = new Blob(chunksRef.current, {
        type: recorder.mimeType || "audio/webm",
      });

      // Preview URL
      const previewUrl = URL.createObjectURL(rawBlob);

      // File to upload
      const wavFile = new File([rawBlob], `recording-${Date.now()}.wav`, {
        type: "audio/wav",
      });

      const newRecording = {
        voiceUrl: previewUrl, // ✅ temporary preview
        file: wavFile,        // ✅ for upload
        toneDescription: "",
        uploading: true,      // show loader while uploading
      };

      // Add to list
      const updated = [...recordings, newRecording];
      onChange(updated);

      // Call parent upload
      if (onUpload) {
        const index = updated.length - 1; // last added
        onUpload(wavFile, index);
      }

      setShowRecorder(false);
    };
  };

  // ✏️ Update tone description
  const updateDescription = (index, desc) => {
    const updated = recordings.map((rec, i) =>
      i === index ? { ...rec, toneDescription: desc } : rec
    );
    onChange(updated);
  };

  // 🗑 Delete
  const deleteRecording = (index) => {
    const updated = recordings.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="p-4 border rounded-lg shadow-sm bg-white  dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
      <p className="text-sm text-gray-600 mb-2 flex items-center gap-2  dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
        <FaMicrophone className="w-5 h-5 text-orange-300" />
        You can add multiple voice recordings, each with a description.
      </p>

      {showRecorder ? (
        <div className="p-4 border rounded-2xl bg-white shadow-sm flex flex-col gap-4 w-full max-w-md  dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {isRecording ? (
                <span className="flex items-center gap-2 text-red-500 font-medium">
                  <LoaderIcon className="w-4 h-4 animate-spin" />
                  Recording in progress...
                </span>
              ) : (
                <span className="flex items-center gap-2 text-gray-600">
                  <CiMicrophoneOn className="w-4 h-4" />
                  Ready
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={startRecording}
              disabled={isRecording}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg shadow transition disabled:opacity-50"
            >
              <CiMicrophoneOn className="w-5 h-5" />
              Start
            </button>
            <button
              type="button"
              onClick={stopRecording}
              disabled={!isRecording}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow transition disabled:opacity-50"
            >
              <CiMicrophoneOff className="w-5 h-5" />
              Stop & Save
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowRecorder(true)}
          className="px-4 py-2 bg-orange-300 text-white rounded"
        >
          + Add Recording
        </button>
      )}

      {/* 🎧 Recordings List */}
      <div className="mt-4 space-y-4  dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
        {recordings.map((rec, index) => (
          <div
            key={index}
            className="p-4 border rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow  dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <div className="flex items-center gap-4  ">
              <audio
                src={rec.voiceUrl}
                controls
                className="flex-1 rounded-md border border-gray-300 bg-gray-100 
             dark:border-gray-600 dark:bg-gray-700"
              />
              {rec.uploading && (
                <LoaderIcon className="w-5 h-5 animate-spin text-gray-500" />
              )}
              <button
                type="button"
                onClick={() => deleteRecording(index)}
                className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition"
              >
                Delete
              </button>
            </div>
            <div className="mt-3">
              <input
                type="text"
                value={rec.toneDescription}
                onChange={(e) => updateDescription(index, e.target.value)}
                placeholder="Enter tone description..."
                className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-400 focus:outline-none  dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};




// const VoiceRecorder = ({ recordings = [], onChange }) => {
//   const [showRecorder, setShowRecorder] = useState(false);
//   const [isRecording, setIsRecording] = useState(false);
//   const mediaRecorderRef = useRef(null);
//   const chunksRef = useRef([]);

//   // 🎤 Start Recording
//   const startRecording = async () => {
//     try {
//       const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
//       const recorder = new MediaRecorder(stream);

//       mediaRecorderRef.current = recorder;
//       chunksRef.current = [];
//       setIsRecording(true);

//       recorder.ondataavailable = (event) => {
//         if (event.data.size > 0) {
//           chunksRef.current.push(event.data);
//         }
//       };

//       recorder.start();
//     } catch (error) {
//       console.error("Mic access denied:", error);
//     }
//   };

//   // 🛑 Stop & Save
//   const stopRecording = () => {
//     const recorder = mediaRecorderRef.current;
//     if (!recorder) return;

//     recorder.stop();
//     setIsRecording(false);

//     recorder.onstop = () => {
//       // Raw blob (webm in Chrome usually)
//       const rawBlob = new Blob(chunksRef.current, {
//         type: recorder.mimeType || "audio/webm",
//       });

//       // ✅ Preview URL for <audio>
//       const previewUrl = URL.createObjectURL(rawBlob);

//       // ✅ Payload file in .wav format
//       const wavFile = new File([rawBlob], `recording-${Date.now()}.wav`, {
//         type: "audio/wav",
//       });

//       const newRecording = {
//         voiceUrl: previewUrl, // for preview
//         file: wavFile, // for upload
//         toneDescription: "",
//       };

//       onChange([...recordings, newRecording]);
//       setShowRecorder(false);
//     };
//   };

//   // ✏️ Update tone description
//   const updateDescription = (index, desc) => {
//     const updated = recordings.map((rec, i) =>
//       i === index ? { ...rec, toneDescription: desc } : rec
//     );
//     onChange(updated);
//   };

//   // 🗑 Delete
//   const deleteRecording = (index) => {
//     const updated = recordings.filter((_, i) => i !== index);
//     onChange(updated);
//   };

//   return (
//     <div className="p-4 border rounded-lg shadow-sm bg-white">
//       <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
//         <FaMicrophone className="w-5 h-5 text-orange-300" />
//         You can add multiple voice recordings, each with a description.
//       </p>

//       {showRecorder ? (
//         <div className="p-4 border rounded-2xl bg-white shadow-sm flex flex-col gap-4 w-full max-w-md">
//           <div className="flex items-center justify-between">
//             <p className="text-sm text-gray-500">
//               {isRecording ? (
//                 <span className="flex items-center gap-2 text-red-500 font-medium">
//                   <LoaderIcon className="w-4 h-4 animate-spin" />
//                   Recording in progress...
//                 </span>
//               ) : (
//                 <span className="flex items-center gap-2 text-gray-600">
//                   <CiMicrophoneOn className="w-4 h-4" />
//                   Ready
//                 </span>
//               )}
//             </p>
//           </div>
//           <div className="flex gap-3">
//             <button
//               type="button"
//               onClick={startRecording}
//               disabled={isRecording}
//               className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg shadow transition disabled:opacity-50"
//             >
//               <CiMicrophoneOn className="w-5 h-5" />
//               Start
//             </button>
//             <button
//               type="button"
//               onClick={stopRecording}
//               disabled={!isRecording}
//               className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg shadow transition disabled:opacity-50"
//             >
//               <CiMicrophoneOff className="w-5 h-5" />
//               Stop & Save
//             </button>
//           </div>
//         </div>
//       ) : (
//         <button
//           type="button"
//           onClick={() => setShowRecorder(true)}
//           className="px-4 py-2 bg-orange-300 text-white rounded"
//         >
//           + Add Recording
//         </button>
//       )}

//       {/* 🎧 Recordings List */}
//       <div className="mt-4 space-y-4">
//         {recordings.map((rec, index) => (
//           <div
//             key={index}
//             className="p-4 border rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
//           >
//             <div className="flex items-center gap-4">
//               <audio
//                 src={rec.voiceUrl}
//                 controls
//                 className="flex-1 rounded-md border bg-gray-100"
//               />
//               <button
//                 type="button"
//                 onClick={() => deleteRecording(index)}
//                 className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition"
//               >
//                 Delete
//               </button>
//             </div>
//             <div className="mt-3">
//               <input
//                 type="text"
//                 value={rec.toneDescription}
//                 onChange={(e) => updateDescription(index, e.target.value)}
//                 placeholder="Enter tone description..."
//                 className="w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-400 focus:outline-none"
//               />
//             </div>
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// };






export default VoiceRecorder;
